
/**
 * Module dependencies.
 */

import 'react-datepicker/dist/react-datepicker.css';
import { Box } from 'react-components/layout';
import { FieldError } from '../../forms/field-error';
import { FormControlWrapper, FormGroup, Label } from '../../forms/fields-styled-components';
import { Icon } from 'react-components/media';
import { color, media } from 'react-components/styles';
import { ifProp, switchProp, theme } from 'styled-tools';
import { isEmpty, range } from 'lodash';
import { typography } from '../../typography/typography-styles';
import { useField, useFormState } from '@seegno/react-forms';
import { useMemo, useState } from 'react';
import { useTranslate } from '../../../i18n';
import DatePicker from 'react-datepicker';
import calendarIcon from '../../assets/svg/32/calendar.svg';
import caretIcon from '../../assets/svg/12/caret.svg';
import moment from 'moment';
import styled, { css } from 'styled-components';

/**
 * `Align` type.
 */

type Align = 'left' | 'right';

/**
 * `Props` type.
 */

type Props = {
  align?: Align;
  dateFormat?: string;
  disabled?: boolean;
  id?: string;
  label: string;
  locale?: string;
  maxDate?: Date;
  minDate?: Date;
  minYear?: number;
  name: string;
};

/**
 * Months.
 */

const months = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
];

/**
 * Date regex.
 */

const dateRegex = '^(0?[1-9]|[12][0-9]|3[01])[\\/](0?[1-9]|1[012])[\\/]\\d{4}$';

/**
 * `DatePickerWrapper` styled component.
 */

const DatePickerWrapper = styled.div<{ align: Align }>`
  align-items: center;
  column-gap: 8px;
  display: grid;
  height: 50px;
  position: relative;
  width: 100%;

  ${switchProp('align', {
    left: css`
      grid-template-columns: 8px 24px 1fr;
    `,
    right: css`
      grid-template-columns: 1fr 24px 8px;
    `
  })}

  ${media.min('md')`
    height: 54px;
  `}

  .react-datepicker {
    ${typography.styles.textSmall};

    border: 1px solid ${color('gray200')};
    border-radius: 12px;
    color: ${color('gray600')};

    .react-datepicker__triangle {
      &::after {
        border-bottom-color: ${color('gray200')};
      }

      &::before {
        border-bottom-color: ${color('gray200')};
      }

      ${switchProp('align', {
    // eslint-disable-next-line indent
        left: css`
          transform: translate3d(56px, 0, 0) !important;
        `,
    // eslint-disable-next-line indent
        right: css`
          transform: translate3d(32px, 0, 0) !important;
        `
    // eslint-disable-next-line indent
      })}
    }
  }

  .react-datepicker-popper {
    left: -1px !important;
    transform: translate3d(0, 56px, 0) !important;
    z-index: ${theme('zIndex.dateInput.popper')};
  }

  .react-datepicker__header {
    background-color: ${color('gray300')};
    border-bottom: 1px solid ${color('gray200')};

    &--custom {
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }

    &:not(.react-datepicker__header--has-time-select) {
      border-top-right-radius: 12px;
    }
  }

  .react-datepicker__input-container input {
    background: none;
    border: none;
    color: ${color('secondary')};
    outline: none;
    transition: opacity ${theme('animations.defaultTransition')};
    width: 100%;

    &:disabled {
      opacity: 0.55;
    }

    ${switchProp('align', {
    // eslint-disable-next-line indent
      left: css`
        padding: 12px 24px 12px 48px;
      `,
    // eslint-disable-next-line indent
      right: css`
        padding: 12px 0 12px 20px;
      `
    // eslint-disable-next-line indent
    })}
  }

  .react-datepicker__day-name {
    color: ${color('gray900')};
  }

  .react-datepicker__day {
    color: ${color('gray900')};

    &:hover {
      background-color: ${color('gray300')};
    }

    &--selected {
      background-color: ${color('gray400')};
      color: ${color('gray900')};
    }

    &--disabled {
      color: ${color('gray400')};
    }

    &--keyboard-selected {
      background-color: transparent;

      &:hover {
        background-color: ${color('gray300')} !important;
      }
    }
  }
`;

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled(Label)<{ align: Align; isHidden: boolean }>`
  top: 10px;

  ${ifProp('isHidden', css`
    clip-path: inset(50%);
  `)}

  ${switchProp('align', {
    left: css`
      left: 48px;
    `,
    right: css`
      left: 20px;
      right: 48px;
    `
  })}
`;

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)<{ align: Align; disabled: boolean }>`
  grid-row: 1;
  transition: color ${theme('animations.defaultTransition')};

  ${ifProp('disabled', css`
    color: ${color('gray400')};
  `)}

  ${switchProp('align', {
    left: css`
      grid-column: 2 / 3;
    `,
    right: css`
      grid-column: -3 / -2;
    `
  })}
`;

/**
 * `CalendarHeader` styled component.
 */

const CalendarHeader = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  padding: 0 16px;
`;

/**
 * Button style.
 */

const buttonStyle = css`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  color: ${color('gray400')};
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  transition: color ${theme('animations.defaultTransition')};
  width: 32px;

  &:focus {
    outline: none;
  }

  &:disabled,
  &:focus,
  &:hover {
    color: ${color('gray600')};
  }
`;

/**
 * `PreviousButton` styled component.
 */

const PreviousButton = styled.button.attrs({ type: 'button' })`
  ${buttonStyle}

  transform: rotate(90deg);
`;

/**
 * `CaretIcon` styled component.
 */

const CaretIcon = styled(Icon).attrs({ 'aria-hidden': true, icon: caretIcon, size: '16px' })`
  color: currentColor;
`;

/**
 * `DateSelectWrapper` styled component.
 */

const DateSelectWrapper = styled.div`
  align-items: center;
  column-gap: 16px;
  display: grid;
  grid-template-columns: repeat(2, min-content);
`;

/**
 * `SelectWrapper` styled component.
 */

const SelectWrapper = styled.div`
  align-items: center;
  display: flex;
  position: relative;
`;

/**
 * `Select` styled component.
 */

const Select = styled.select`
  ${typography.styles.textSmall};

  appearance: none;
  background: none;
  border: none;
  color: ${color('gray900')};
  outline: none;
  padding-right: 12px;
`;

/**
 * `SelectCaretIcon` styled component.
 */

const SelectCaretIcon = styled(Icon).attrs({ 'aria-hidden': true, icon: caretIcon, size: '12px' })`
  pointer-events: none;
  position: absolute;
  right: 0;
`;

/**
 * `NextButton` styled component.
 */

const NextButton = styled.button.attrs({ type: 'button' })`
  ${buttonStyle}

  transform: rotate(-90deg);
`;

/**
 * Export `DateInput` component.
 */

export function DateInput(props: Props): JSX.Element {
  const {
    align = 'left',
    dateFormat: dateFormatValue,
    disabled = false,
    id,
    label,
    locale,
    maxDate = new Date(),
    minYear = 1970,
    name,
    ...rest
  } = props;

  const { translate } = useTranslate();
  const { error, meta, onBlur, onChange, value = '', ...fieldProps } = useField(name);
  const dateFormat = dateFormatValue ?? moment.localeData().longDateFormat('L');
  const { active } = meta;
  const { alreadySubmitted } = useFormState();
  const hasError = !!error && alreadySubmitted;
  const fieldValue = !isEmpty(value) ? moment(value, dateFormat).toDate() : null;
  const [dateValue, setDateValue] = useState(fieldValue);
  const [inputValue, setInputValue] = useState();
  const hasValue = !!value;
  const years = useMemo(() => {
    return range(minYear, maxDate.getFullYear() + 1, 1);
  }, [maxDate, minYear]);

  const monthsOptions = useMemo(() => {
    return months.map(month => ({
      label: translate(`months.${month}`),
      value: month
    }));
  }, [translate]);

  return (
    <FormGroup
      disabled={disabled}
      hasAlert={hasError}
    >
      <FormControlWrapper
        disabled={disabled}
        hasError={hasError}
        hasFocus={active}
      >
        <DatePickerWrapper align={align}>
          <StyledIcon
            align={align}
            aria-hidden
            color={color('gray500')}
            disabled={disabled}
            icon={calendarIcon}
            size={'24px'}
          />

          <Box
            gridColumn={'1 / -1'}
            gridRow={1}
            position={'relative'}
          >
            <DatePicker
              {...rest}
              {...fieldProps}
              adjustDateOnChange={false}
              dateFormat={'dd/MM/yyyy'}
              disabled={disabled}
              id={id ?? name}
              locale={locale}
              maxDate={maxDate}
              name={name}
              onBlur={() => onBlur()}
              onCalendarClose={() => {
                onBlur();

                if (isEmpty(value)) {
                  setInputValue(undefined);
                }
              }}
              onChangeRaw={(event: any) => {
                const value = event.target.value;

                setInputValue(value);

                if (!isEmpty(value) && !value.match(dateRegex)) {
                  return;
                }

                if (isEmpty(value)) {
                  setDateValue(null);
                  onChange(undefined);
                } else {
                  const [day, month, year] = value.split('/');
                  const date = `${year}-${month}-${day}`;

                  if (!years.includes(Number(year))) {
                    return;
                  }

                  setDateValue(new Date(date));
                  onChange(moment(date).format(dateFormat));
                }
              }}
              onSelect={(date: any) => {
                setDateValue(date);
                onChange(moment(date).format(dateFormat));
              }}
              renderCustomHeader={({
                changeMonth,
                changeYear,
                date,
                decreaseMonth,
                increaseMonth,
                nextMonthButtonDisabled,
                prevMonthButtonDisabled
              }: any) => (
                <CalendarHeader>
                  <PreviousButton
                    aria-label={translate('calendar.actions.previous')}
                    disabled={prevMonthButtonDisabled}
                    onClick={decreaseMonth}
                  >
                    <CaretIcon />
                  </PreviousButton>

                  <DateSelectWrapper>
                    <SelectWrapper>
                      <SelectCaretIcon />

                      <Select
                        aria-label={translate('calendar.labels.month')}
                        onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                        value={months[date.getMonth()]}
                      >
                        {monthsOptions.map(({ label, value }, index) => (
                          <option
                            key={index}
                            value={value}
                          >
                            {label}
                          </option>
                        ))}
                      </Select>
                    </SelectWrapper>

                    <SelectWrapper>
                      <SelectCaretIcon />

                      <Select
                        aria-label={translate('calendar.labels.year')}
                        onChange={({ target: { value } }) => changeYear(Number(value))}
                        value={date.getFullYear()}
                      >
                        {years.map(option => (
                          <option
                            key={option}
                            value={option}
                          >
                            {option}
                          </option>
                        ))}
                      </Select>
                    </SelectWrapper>
                  </DateSelectWrapper>

                  <NextButton
                    aria-label={translate('calendar.actions.next')}
                    disabled={nextMonthButtonDisabled}
                    onClick={increaseMonth}
                  >
                    <CaretIcon />
                  </NextButton>
                </CalendarHeader>
              )}
              selected={dateValue}
            />

            <StyledLabel
              align={align}
              disabled={disabled}
              htmlFor={id ?? name}
              isHidden={hasValue || !!inputValue}
            >
              {label}
            </StyledLabel>
          </Box>
        </DatePickerWrapper>
      </FormControlWrapper>

      <FieldError
        error={error}
        isOpen={!!error}
        name={name}
      />
    </FormGroup>
  );
}
